/**
 * The payroll domain's client-side vocabulary — the label/badge map for a run's
 * `status` (the two-step approval state machine), the label map for a payslip's
 * named components (the Vietnamese payroll terms of
 * `docs/payroll/SAMPLE_PAYROLL_ACHAU_VI.md`), a small VND formatter, and the map that
 * says which components that formatter must not touch because they are not money.
 *
 * Nothing here decides anything about a request. The labels turn the contract's
 * enums and field names into `MessageKey`s so the screen never hard-codes a status
 * word or a component name, the same discipline `hr/leave.ts` and `hr/performance.ts`
 * keep for their own domains.
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type { IconName } from '../states/icons';
import type { PayrollComponents, PayrollPeriodStatus, PayrollRunStatus } from '../api/payroll';

/** `PayrollPeriod.status` — `draft` / `open` / `closed`, the same three values and the same cascade `ReviewCycleStatus` uses in `hr/performance.ts`: `draft` quietest, `open` the live one, `closed` settled. */
export const PERIOD_STATUS_LABEL: Readonly<Record<PayrollPeriodStatus, MessageKey>> = {
  draft: 'payroll.periodStatus.draft',
  open: 'payroll.periodStatus.open',
  closed: 'payroll.periodStatus.closed',
};

export const PERIOD_STATUS_BADGE: Readonly<Record<PayrollPeriodStatus, Severity>> = {
  draft: 'info',
  open: 'low',
  closed: 'medium',
};

/** One row of the run-status table — severity, icon and label, exactly `attendance/status.ts`'s `StatusPresentation` shape. */
export interface RunStatusPresentation {
  /** Chooses the colour token. The third channel — never the first, never the only. */
  readonly severity: Severity;
  /** The silhouette. Distinct across all five states. */
  readonly icon: IconName;
  readonly labelKey: MessageKey;
}

/**
 * `draft -> calculated -> manager_approved -> ceo_approved -> paid`
 * (`src/modules/payroll/service.ts`'s own header names this chain). `draft` and
 * `calculated` are `info`/`medium` — nothing is final yet; `manager_approved` is
 * still `medium` — one signature short of official; `ceo_approved` and `paid` are
 * `low` — the number is now official and, for `paid`, settled. Five distinct icons
 * carry the actual meaning: `Badge` never renders colour without them.
 */
export const RUN_STATUS_PRESENTATION: Readonly<Record<PayrollRunStatus, RunStatusPresentation>> = {
  draft: { severity: 'info', icon: 'sliders-horizontal', labelKey: 'payroll.runStatus.draft' },
  calculated: { severity: 'medium', icon: 'clock', labelKey: 'payroll.runStatus.calculated' },
  manager_approved: {
    severity: 'medium',
    icon: 'circle-check',
    labelKey: 'payroll.runStatus.manager_approved',
  },
  ceo_approved: { severity: 'low', icon: 'lock', labelKey: 'payroll.runStatus.ceo_approved' },
  paid: { severity: 'low', icon: 'inbox', labelKey: 'payroll.runStatus.paid' },
};

/**
 * The name of every field of `PayrollComponents` (`src/modules/payroll/engine.ts`),
 * in the same order the engine and `docs/payroll/SAMPLE_PAYROLL_ACHAU_VI.md` list
 * them — Lương CB, Lương chính theo HD, Giờ công, Lương chính, Lương năng suất HD,
 * Tỷ lệ đạt, Lương Hiệu quả, Giờ OT, Lương OT, Lương, three Phụ cấp lines plus their
 * total, Thưởng, Phạt, the employee-side and employer-side insurance lines plus
 * their totals, Tổng lương, Tổng chi trả, Tạm ứng, Thực lãnh.
 */
export const PAYROLL_COMPONENT_LABEL: Readonly<Record<keyof PayrollComponents, MessageKey>> = {
  luong_cb: 'payroll.component.luong_cb',
  luong_chinh_theo_hd: 'payroll.component.luong_chinh_theo_hd',
  gio_cong: 'payroll.component.gio_cong',
  luong_chinh: 'payroll.component.luong_chinh',
  luong_nang_suat_hd: 'payroll.component.luong_nang_suat_hd',
  ty_le_dat: 'payroll.component.ty_le_dat',
  luong_hieu_qua: 'payroll.component.luong_hieu_qua',
  gio_ot: 'payroll.component.gio_ot',
  luong_ot: 'payroll.component.luong_ot',
  luong: 'payroll.component.luong',
  phu_cap_dong_phuc: 'payroll.component.phu_cap_dong_phuc',
  phu_cap_xang: 'payroll.component.phu_cap_xang',
  phu_cap_khac: 'payroll.component.phu_cap_khac',
  tong_phu_cap: 'payroll.component.tong_phu_cap',
  thuong: 'payroll.component.thuong',
  phat: 'payroll.component.phat',
  khau_tru_boi_thuong: 'payroll.component.khau_tru_boi_thuong',
  giam_thuong: 'payroll.component.giam_thuong',
  khau_tru_toi_da: 'payroll.component.khau_tru_toi_da',
  bhxh_nv: 'payroll.component.bhxh_nv',
  bhyt_nv: 'payroll.component.bhyt_nv',
  kpcd_nv: 'payroll.component.kpcd_nv',
  tong_bhnv: 'payroll.component.tong_bhnv',
  bhxh_cty: 'payroll.component.bhxh_cty',
  bhyt_cty: 'payroll.component.bhyt_cty',
  kpcd_cty: 'payroll.component.kpcd_cty',
  tong_bh_cty: 'payroll.component.tong_bh_cty',
  tong_bh_kpcd: 'payroll.component.tong_bh_kpcd',
  tong_luong: 'payroll.component.tong_luong',
  tong_chi_tra: 'payroll.component.tong_chi_tra',
  tam_ung: 'payroll.component.tam_ung',
  thuc_lanh: 'payroll.component.thuc_lanh',
};

/**
 * The order a payslip's components are rendered in — `PAYROLL_COMPONENT_LABEL`'s own key
 * order, kept as a separate array because object key order is a detail this file should
 * not lean on silently.
 *
 * Two keys are deliberately NOT here (attendance-core Task 5, fix round 1 C2):
 *
 *  - `phat` is the echoed raw input, not a component of the answer, and showing it
 *    beside the two things it became (`khau_tru_boi_thuong`, `giam_thuong`) would show
 *    the same money twice — the same reason `payslip-pdf.ts`'s `PAYSLIP_LINES` leaves
 *    it out.
 *  - `khau_tru_toi_da` is a CEILING, not money that moved. `MyPayslipPanel` renders
 *    this whole list as one additive row of figures with no "Loại" (kind) column to
 *    mark it apart the way the PDF's `'limit'` line type does — printed in that list
 *    it reads as a second deduction stacked on the one it bounds, and an employee
 *    adding up their own rows cannot reconcile them to net pay. `MyPayslipPanel`
 *    prints it as its own note instead, beside the deduction it bounds, only when a
 *    `capped` ground's deduction actually landed on the payslip.
 *
 * Both stay in `PAYROLL_COMPONENT_LABEL` only because that map is typed over every key
 * of `PayrollComponents`.
 */
export const PAYROLL_COMPONENT_ORDER: readonly (keyof PayrollComponents)[] = [
  'luong_cb',
  'luong_chinh_theo_hd',
  'gio_cong',
  'luong_chinh',
  'luong_nang_suat_hd',
  'ty_le_dat',
  'luong_hieu_qua',
  'gio_ot',
  'luong_ot',
  'luong',
  'phu_cap_dong_phuc',
  'phu_cap_xang',
  'phu_cap_khac',
  'tong_phu_cap',
  'thuong',
  'giam_thuong',
  'khau_tru_boi_thuong',
  'bhxh_nv',
  'bhyt_nv',
  'kpcd_nv',
  'tong_bhnv',
  'bhxh_cty',
  'bhyt_cty',
  'kpcd_cty',
  'tong_bh_cty',
  'tong_bh_kpcd',
  'tong_luong',
  'tong_chi_tra',
  'tam_ung',
  'thuc_lanh',
];

/**
 * Currencies whose smallest unit is the unit itself — a payslip in đồng, yen or won
 * with two decimals is a document nobody in those countries has seen.
 *
 * A short table rather than `Intl.NumberFormat(...).resolvedOptions()`, for the
 * reason `formatAmount` below records: the grouping here must be identical on every
 * machine, and a small-ICU build answers "two decimals" for everything.
 * `src/lib/money.ts` holds the server's copy of the same fact; the two are checked
 * against each other by eye rather than shared, because this bundle has no access to
 * `src/`.
 */
const ZERO_SUBUNIT_CURRENCIES: ReadonlySet<string> = new Set([
  'VND',
  'JPY',
  'KRW',
  'IDR',
  'LAK',
  'KHR',
  'MMK',
]);

/**
 * One figure, comma-grouped, in the precision its currency is counted in —
 * `16,692,308` for đồng, `1,234.50` for dollars. The exact separator
 * `docs/payroll/SAMPLE_PAYROLL_ACHAU_VI.md` uses.
 *
 * Written out with a regular expression rather than `toLocaleString`, so the
 * grouping is the same in every test environment regardless of which ICU data
 * happens to be linked in — a formatter whose output depends on the machine it runs
 * on is not a formatter this screen's tests can assert against.
 *
 * **No currency code in the output.** This is the figure for a table column that is
 * already headed with the currency; repeating the code on all twenty-nine rows of a
 * payslip is noise in the one place figures have to be comparable down the page.
 * `formatMoney` is the variant for an amount standing alone in a sentence.
 *
 * A non-finite value renders as zero rather than `NaN`, and an amount that rounds
 * away to nothing never prints as `-0`.
 */
export function formatAmount(value: number, currency: string): string {
  const subunits = ZERO_SUBUNIT_CURRENCIES.has(currency) ? 0 : 2;
  const safe = Number.isFinite(value) ? value : 0;
  const scale = 10 ** subunits;
  // `.toFixed(6)` before the rounding, because `1.005 * 100` is `100.49999999999999`
  // in IEEE-754 and half-up on the raw product would print `1.00` for an amount a
  // person typed as `1.005`. Six digits is far below the noise floor of any currency
  // and far above the error the multiplication introduces.
  const rounded = Math.round(Number((safe * scale).toFixed(6))) / scale;
  const negative = rounded < 0;
  const fixed = Math.abs(rounded).toFixed(subunits);
  const [whole = '0', fraction] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = fraction === undefined ? grouped : `${grouped}.${fraction}`;
  return negative ? `-${body}` : body;
}

/**
 * The same figure with its ISO code after it — `16,692,308 VND`.
 *
 * The **code**, never a symbol: `₫`, `$` and `¥` are each shared by several
 * currencies, and a payslip is exactly the document where "which dollar" must not be
 * a guess. `src/lib/money.ts` prints the server's copy of this string the same way,
 * so the payslip PDF and these screens agree character for character.
 */
export function formatMoney(value: number, currency: string): string {
  return `${formatAmount(value, currency)} ${currency}`;
}

/**
 * A figure that is đồng **by law** rather than by configuration.
 *
 * Kept, and deliberately not widened to take a currency: the progressive PIT tariff
 * and the BHXH/BHYT caps on the formulas screen are Vietnamese statute in VND
 * (`src/modules/payroll/vn-2026.ts`, unchanged by Task CS-1), and re-labelling those
 * brackets with a company's chosen currency would state a number in a unit it was
 * never written in. Money that belongs to the company goes through `formatAmount` /
 * `formatMoney` with the company's own currency.
 */
export function formatVnd(value: number): string {
  return formatAmount(value, 'VND');
}

/**
 * Which unit each member of `PayrollComponents` is in — because three of them are not
 * money, and `formatVnd` opens with `Math.round`.
 *
 * QA-G-032: the payslip formatted the whole list as đồng, so `ty_le_dat` 0.95 printed
 * as `1` — an employee who reached 95% of target was told on the document that explains
 * their pay that they reached 100%, beside a `luong_hieu_qua` computed from 0.95 that no
 * longer added up. `gio_cong` and `gio_ot` are the same mistake with a smaller blast
 * radius: a 168.5-hour month rounds to 169.
 *
 * A `Record` over `keyof PayrollComponents`, so a component the engine adds has to say
 * which of the three it is rather than defaulting into money.
 */
export type PayrollComponentUnit = 'vnd' | 'ratio' | 'hours';

export const PAYROLL_COMPONENT_UNIT: Readonly<
  Record<keyof PayrollComponents, PayrollComponentUnit>
> = {
  luong_cb: 'vnd',
  luong_chinh_theo_hd: 'vnd',
  gio_cong: 'hours',
  luong_chinh: 'vnd',
  luong_nang_suat_hd: 'vnd',
  ty_le_dat: 'ratio',
  luong_hieu_qua: 'vnd',
  gio_ot: 'hours',
  luong_ot: 'vnd',
  luong: 'vnd',
  phu_cap_dong_phuc: 'vnd',
  phu_cap_xang: 'vnd',
  phu_cap_khac: 'vnd',
  tong_phu_cap: 'vnd',
  thuong: 'vnd',
  phat: 'vnd',
  khau_tru_boi_thuong: 'vnd',
  giam_thuong: 'vnd',
  khau_tru_toi_da: 'vnd',
  bhxh_nv: 'vnd',
  bhyt_nv: 'vnd',
  kpcd_nv: 'vnd',
  tong_bhnv: 'vnd',
  bhxh_cty: 'vnd',
  bhyt_cty: 'vnd',
  kpcd_cty: 'vnd',
  tong_bh_cty: 'vnd',
  tong_bh_kpcd: 'vnd',
  tong_luong: 'vnd',
  tong_chi_tra: 'vnd',
  tam_ung: 'vnd',
  thuc_lanh: 'vnd',
};

/**
 * A figure that is **not** money, printed as it stands: `0.95`, `168.5`, `7.25`.
 *
 * Never rounded — that is the whole defect — and never re-scaled into a percentage
 * either: the run-inputs grid asks for `ty_le_dat` as the fraction it is (`0.9 = 90%`),
 * and a payslip showing `95%` where the person typed `0.95` would be a third spelling of
 * one number. `toFixed(6)` only keeps binary-float noise off the screen, the same way
 * `percentOf` uses it in the formulas panel.
 */
function formatExactFigure(value: number): string {
  return String(Number(value.toFixed(6)));
}

/**
 * One payslip component's figure, in whatever unit that component is actually in.
 *
 * The `'vnd'` unit name is historical — it means "this component is money", and the
 * money is the **company's** currency (Task CS-1), not necessarily đồng. The statutory
 * VND figures that really are đồng by law go through `formatVnd` instead, on the
 * formulas screen.
 */
export function formatPayrollComponent(
  key: keyof PayrollComponents,
  value: number,
  currency: string,
): string {
  return PAYROLL_COMPONENT_UNIT[key] === 'vnd'
    ? formatAmount(value, currency)
    : formatExactFigure(value);
}
